import React, { useState, useEffect, useCallback } from 'react';
import QuestionLearnButton from '../full_mock_test/attempt/QuestionLearnButton';
import '../../styles/ExerciseLayout.css';

const GapFillQuestion = ({ question, questionNumber, isExample, answer, handleChange, showHint, isReviewMode, isCorrect, isHydrated }) => {
  const rawText = question.question_text.replace(/\(Example\)/gi, '').trim();
  const parts = rawText.split('{{big_gap}}');
  const before = parts[0] || '';
  const after = parts[1] || '';

  const isAnswerCorrect = isCorrect === true || isCorrect === 'true';
  const showIncorrect = isReviewMode && !isAnswerCorrect;

  return (
    <div className={`flex items-start gap-3 p-3 rounded-2xl border-2 transition-all ${isExample ? 'bg-blue-50/50 border-blue-100' : 'bg-white border-slate-100 shadow-sm'}`}>
      <span className={`shrink-0 font-black px-2.5 py-1 rounded-xl text-sm border shadow-sm mt-0.5 ${isExample ? 'text-white bg-amber-500 border-amber-500' : 'text-slate-400 bg-slate-50 border-slate-100'}`}>
        {isExample ? 'e.g.' : questionNumber}
      </span>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-slate-700 text-sm leading-relaxed m-0 inline">
          {before}
        </p>
        {showIncorrect ? (
          <span className="inline-flex items-center bg-red-50 px-2 py-0.5 rounded-lg border border-red-100 mx-1 align-middle">
            {question.user_answer?.answer
              ? <span className="text-red-500 line-through decoration-2 mr-2 font-bold text-sm">{question.user_answer.answer}</span>
              : <span className="text-gray-400 mr-2 font-bold">__</span>
            }
            <span className="text-green-600 font-bold text-sm">{question.accepted_answers[0]}</span>
          </span>
        ) : (
          <input
            type="text"
            value={isExample ? (question.accepted_answers && question.accepted_answers[0]) || '' : (showHint || (isReviewMode && isAnswerCorrect)) ? question.accepted_answers[0] : answer || ''}
            onChange={isExample ? undefined : (e) => handleChange(question.id, e.target.value)}
            readOnly={isExample || showHint || !isHydrated}
            disabled={isReviewMode || !isHydrated}
            className={`exercise-input-field exercise-input-text !w-[140px] text-center mx-1 align-middle text-sm px-1 py-1 font-bold !rounded-xl ${isExample ? 'exercise-example-text' : ''} ${showHint && !isExample ? 'hint-input' : ''}`}
            autoComplete="off"
          />
        )}
        {after && <p className="font-bold text-slate-700 text-sm leading-relaxed m-0 inline">{after}</p>}
        {isReviewMode && !isExample && (
          <span className="inline-flex items-center gap-1 ml-1 align-middle">
            {isAnswerCorrect
              ? <span className="correct-checkmark scale-75">✓</span>
              : <span className="incorrect-checkmark scale-75">✗</span>
            }
            <QuestionLearnButton question={question} size={18} />
          </span>
        )}
      </div>
    </div>
  );
};

const StartersReadingPart5 = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal }) => {
  const [answers, setAnswers] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (attemptId && exerciseId) {
      if (isReviewMode && exerciseData.questions) {
        const migratedAnswers = {};
        exerciseData.questions.forEach(q => {
          if (q.user_answer && typeof q.user_answer.answer === 'string') {
            migratedAnswers[q.id] = q.user_answer.answer;
          }
        });
        if (Object.keys(migratedAnswers).length > 0) {
          setAnswers(migratedAnswers);
          setIsHydrated(true);
          return;
        }
      }

      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedData = allAnswers[exerciseId];
          if (savedData) {
            const migratedAnswers = {};
            Object.keys(savedData).forEach(qId => {
              const value = savedData[qId];
              migratedAnswers[qId] = (typeof value === 'object' && value !== null && 'answer' in value) ? value.answer : value;
            });
            setAnswers(migratedAnswers);
          }
        } catch (e) {
          console.error("Failed to parse saved attempt from localStorage", e);
        }
      }
      setIsHydrated(true);
    }
  }, [attemptId, exerciseId, isReviewMode, exerciseData.questions]);

  const saveAnswersToLocalStorage = useCallback((currentAnswers) => {
    if (!isHydrated || !attemptId || !exerciseId || isReviewMode) return;

    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allAnswers = JSON.parse(savedAttempt) || {};
    } catch (e) {
      allAnswers = {};
    }

    const answersToSave = {};
    Object.keys(currentAnswers).forEach(qId => {
      answersToSave[qId] = { answer: currentAnswers[qId] };
    });

    allAnswers[exerciseId] = answersToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated, isReviewMode]);

  useEffect(() => {
    if (fillHintSignal > 0) {
      const newAnswers = {};
      exerciseData.questions.forEach(q => {
        if (q.accepted_answers && q.accepted_answers.length > 0) {
          newAnswers[q.id] = q.accepted_answers[0];
        }
      });
      setAnswers(newAnswers);
      saveAnswersToLocalStorage(newAnswers);
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage]);

  useEffect(() => {
    if (!isHydrated) return;
    const debounceSave = setTimeout(() => {
      saveAnswersToLocalStorage(answers);
    }, 500);
    return () => {
      clearTimeout(debounceSave);
      saveAnswersToLocalStorage(answers);
    };
  }, [answers, isHydrated, saveAnswersToLocalStorage]);

  if (!exerciseData || !exerciseData.content_data || !exerciseData.questions) {
    return <div>Loading exercise data...</div>;
  }

  const { reading_text = '' } = exerciseData.content_data;
  const ASSET_BASE_URL = process.env.REACT_APP_MEDIA_PREFIX || 'http://localhost:8000';
  const resolveUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `${ASSET_BASE_URL}${url}`;
  };

  const sortedQuestions = [...exerciseData.questions].sort((a, b) => a.order_index - b.order_index);

  // Build sections: group questions by section markers (questions with options containing image_url)
  const sections = [];
  // First section: exercise-level image + reading_text
  sections.push({
    imageUrl: null,
    paragraphText: reading_text,
    questions: [],
  });

  for (const q of sortedQuestions) {
    const opt = q.options && q.options[0];
    const hasSection = opt && typeof opt === 'object' && opt.image_url;
    if (hasSection) {
      // This question starts a new section
      const pText = opt.paragraph_text || opt['paragraph_text.'] || '';
      sections.push({
        imageUrl: opt.image_url,
        paragraphText: pText,
        questions: [q],
      });
    } else {
      // Add to current section
      sections[sections.length - 1].questions.push(q);
    }
  }

  const handleChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  // Track question numbering (skip examples)
  let questionCounter = 0;

  return (
    <div className="w-full h-full p-1 md:px-10 overflow-y-auto custom-scrollbar">
      <div className="max-w-3xl mx-auto w-full py-4 space-y-6">
        {sections.map((section, sIdx) => (
          <div key={sIdx} className="space-y-4">
            {/* Section image */}
            {section.imageUrl && (
              <div className="flex justify-center">
                <img
                  src={resolveUrl(section.imageUrl)}
                  alt="Story illustration"
                  className="rounded-2xl max-w-full max-h-56 object-contain shadow-sm border border-gray-100 p-1 bg-white"
                />
              </div>
            )}

            {/* Section paragraph */}
            {section.paragraphText && (
              <div className="bg-white rounded-2xl border border-slate-100 p-4 md:p-6 shadow-sm">
                <div className="text-sm leading-relaxed text-slate-700 space-y-3">
                  {section.paragraphText.split(/\\n\\n|\n\n/).map((para, pIdx) => (
                    <p key={pIdx} className="m-0">{para.replace(/\\n/g, '\n')}</p>
                  ))}
                </div>
              </div>
            )}

            {/* Section questions */}
            {section.questions.length > 0 && (
              <div className="space-y-2">
                {section.questions.map(q => {
                  const isExample = q.points === 0;
                  if (!isExample) questionCounter++;
                  return (
                    <GapFillQuestion
                      key={q.id}
                      question={q}
                      questionNumber={isExample ? null : questionCounter}
                      isExample={isExample}
                      answer={answers[q.id]}
                      handleChange={handleChange}
                      showHint={showHint}
                      isReviewMode={isReviewMode}
                      isCorrect={q.is_correct}
                      isHydrated={isHydrated}
                    />
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default StartersReadingPart5;
